import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import Modal from './Modal'
import { useAuth } from '../contexts/AuthContext'
import { useLanguage } from '../contexts/LanguageContext'
import { updatePreferences, requestEmailChange, cancelEmailChange } from '../services/users'
import userAttributesService from '../services/userAttributes'
import UserAttributeFields from './userAttributes/UserAttributeFields'
import { initialValuesFor, changedValues } from './userAttributes/userAttributeFields'
import './ProfileModal.scss'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const ProfileModal = ({ isOpen, onClose, profile, onSave }) => {
  const { t } = useTranslation(['common', 'auth'])
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [selectedLanguage, setSelectedLanguage] = useState('en')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const { refreshProfile } = useAuth()
  const refreshProfileRef = useRef(refreshProfile)
  refreshProfileRef.current = refreshProfile
  const { currentLanguage, changeLanguage, supportedLanguages } = useLanguage()

  // Email-change (#312) state
  const [showEmailChange, setShowEmailChange] = useState(false)
  const [newEmail, setNewEmail] = useState('')
  const [emailError, setEmailError] = useState(null)
  const [emailSuccess, setEmailSuccess] = useState(null)
  const [emailLoading, setEmailLoading] = useState(false)
  const [pendingEmail, setPendingEmail] = useState(null)

  // Custom attributes (#267). Definitions are loaded only while the modal is
  // open; GET /user-attributes already filters out `hidden` ones and
  // anything archived for a non-admin caller, so no client-side filtering is
  // needed here.
  const { data: attributeDefinitions = [] } = useQuery({
    queryKey: ['userAttributes', 'self'],
    queryFn: () => userAttributesService.list(),
    enabled: isOpen,
  })
  const [attributeValues, setAttributeValues] = useState({})

  // Map an API error to a localized, user-facing message (CLAUDE.md: always
  // surface a message; prefer the backend's when present).
  const emailErrorMessage = (err) => {
    const status = err?.response?.status
    const serverMessage = err?.response?.data?.error
    if (status === 409) return t('auth:changeEmail.alreadyInUse')
    if (status === 429) return t('auth:changeEmail.tooSoon')
    if (status === 400) {
      if (/match/i.test(serverMessage || '')) return t('auth:changeEmail.sameAsCurrent')
      return serverMessage || t('auth:changeEmail.invalidEmail')
    }
    return serverMessage || t('auth:changeEmail.requestFailed')
  }

  // Refresh profile from server when modal opens — picks up any permission/capability
  // changes made by an admin while the user was logged in
  useEffect(() => {
    if (isOpen) {
      refreshProfileRef.current()
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && profile) {
      setFirstName(profile.firstName || '')
      setLastName(profile.lastName || '')
      setSelectedLanguage(profile.preferredLanguage || currentLanguage || 'en')
      setError(null)
      // Reset email-change UI to reflect current profile state
      setPendingEmail(profile.pendingEmail || null)
      setShowEmailChange(false)
      setNewEmail('')
      setEmailError(null)
      setEmailSuccess(null)
    }
  }, [isOpen, profile, currentLanguage])

  // Seed the custom-attribute working values once the definitions have
  // loaded (separate effect: definitions arrive asynchronously, after the
  // name/email fields above have already been reset from `profile`).
  useEffect(() => {
    if (isOpen && profile) {
      setAttributeValues(initialValuesFor(attributeDefinitions, profile.attributes || {}))
    }
  }, [isOpen, profile, attributeDefinitions])

  const handleRequestEmailChange = async (emailToSend) => {
    setEmailLoading(true)
    setEmailError(null)
    setEmailSuccess(null)
    const target = (emailToSend || '').toLowerCase().trim()

    // Client-side guards (still surfaced via localized in-page messages)
    if (!EMAIL_REGEX.test(target)) {
      setEmailError(t('auth:changeEmail.invalidEmail'))
      setEmailLoading(false)
      return
    }
    if (target === (profile?.email || '').toLowerCase().trim()) {
      setEmailError(t('auth:changeEmail.sameAsCurrent'))
      setEmailLoading(false)
      return
    }

    try {
      await requestEmailChange(target)
      setPendingEmail(target)
      setEmailSuccess(t('auth:changeEmail.linkSent', { email: target }))
      setShowEmailChange(false)
      setNewEmail('')
      await refreshProfileRef.current()
    } catch (err) {
      setEmailError(emailErrorMessage(err))
    } finally {
      setEmailLoading(false)
    }
  }

  const handleCancelEmailChange = async () => {
    setEmailLoading(true)
    setEmailError(null)
    setEmailSuccess(null)
    try {
      await cancelEmailChange()
      setPendingEmail(null)
      await refreshProfileRef.current()
    } catch (err) {
      setEmailError(err?.response?.data?.error || t('auth:changeEmail.cancelFailed'))
    } finally {
      setEmailLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      // Custom attributes (#267): only send keys the user actually changed;
      // a value cleared back to empty is sent as explicit null.
      const attributeChanges = changedValues(attributeDefinitions, profile?.attributes || {}, attributeValues)
      const profilePayload = { firstName, lastName }
      if (Object.keys(attributeChanges).length > 0) {
        profilePayload.attributes = attributeChanges
      }

      // Save profile (name + any changed attributes)
      await onSave(profilePayload)

      // Save language preference if changed
      if (selectedLanguage !== (profile?.preferredLanguage || currentLanguage)) {
        await updatePreferences({ preferredLanguage: selectedLanguage })
        await changeLanguage(selectedLanguage)
      }

      // PUT /users/me's response omits some fields GET /users/me returns
      // (e.g. capabilities); resync from the authoritative GET so the rest
      // of the app never sees a briefly-incomplete profile.
      await refreshProfileRef.current()

      onClose()
    } catch (err) {
      setError(err.message || t('errors.updateFailed'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('profile.editTitle')} size="sm">
      <form onSubmit={handleSubmit}>
        {error && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            marginBottom: '1.5rem',
            borderRadius: '4px',
            borderLeft: '4px solid var(--color-danger)'
          }}>
            <strong>{t('labels.error')}:</strong> {error}
          </div>
        )}

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: '500',
            color: '#333'
          }}>
            {t('labels.firstName')}
          </label>
          <input
            type="text"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder={t('placeholders.firstName')}
            style={{
              width: '100%',
              padding: '0.625rem 0.875rem',
              fontSize: '0.875rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: '500',
            color: '#333'
          }}>
            {t('labels.lastName')}
          </label>
          <input
            type="text"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder={t('placeholders.lastName')}
            style={{
              width: '100%',
              padding: '0.625rem 0.875rem',
              fontSize: '0.875rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              boxSizing: 'border-box'
            }}
          />
        </div>

        <div className="profile-email-change">
          <label className="profile-email-change__label">
            {t('auth:changeEmail.currentEmailLabel')}
          </label>
          <div className="profile-email-change__current">
            <span className="profile-email-change__current-value">{profile?.email}</span>
            {!pendingEmail && !showEmailChange && (
              <button
                type="button"
                className="profile-email-change__link"
                onClick={() => {
                  setShowEmailChange(true)
                  setEmailError(null)
                  setEmailSuccess(null)
                  setNewEmail('')
                }}
              >
                {t('auth:changeEmail.changeButton')}
              </button>
            )}
          </div>

          {emailSuccess && (
            <div className="profile-email-change__message profile-email-change__message--success">
              {emailSuccess}
            </div>
          )}
          {emailError && (
            <div className="profile-email-change__message profile-email-change__message--error">
              {emailError}
            </div>
          )}

          {pendingEmail && (
            <div className="profile-email-change__pending">
              <p className="profile-email-change__pending-text">
                {t('auth:changeEmail.pendingBanner', { email: pendingEmail })}
              </p>
              <div className="profile-email-change__pending-actions">
                <button
                  type="button"
                  className="profile-email-change__btn profile-email-change__btn--secondary"
                  onClick={() => handleRequestEmailChange(pendingEmail)}
                  disabled={emailLoading}
                >
                  {emailLoading ? t('auth:changeEmail.resending') : t('auth:changeEmail.resend')}
                </button>
                <button
                  type="button"
                  className="profile-email-change__btn profile-email-change__btn--danger"
                  onClick={handleCancelEmailChange}
                  disabled={emailLoading}
                >
                  {emailLoading ? t('auth:changeEmail.cancelling') : t('auth:changeEmail.cancel')}
                </button>
              </div>
            </div>
          )}

          {!pendingEmail && showEmailChange && (
            <div className="profile-email-change__form">
              <label className="profile-email-change__label" htmlFor="profile-new-email">
                {t('auth:changeEmail.newEmailLabel')}
              </label>
              <input
                id="profile-new-email"
                type="email"
                className="profile-email-change__input"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder={t('auth:changeEmail.newEmailLabel')}
              />
              <div className="profile-email-change__form-actions">
                <button
                  type="button"
                  className="profile-email-change__btn profile-email-change__btn--secondary"
                  onClick={() => {
                    setShowEmailChange(false)
                    setNewEmail('')
                    setEmailError(null)
                  }}
                  disabled={emailLoading}
                >
                  {t('buttons.cancel')}
                </button>
                <button
                  type="button"
                  className="profile-email-change__btn profile-email-change__btn--primary"
                  onClick={() => handleRequestEmailChange(newEmail)}
                  disabled={emailLoading}
                >
                  {emailLoading ? t('auth:changeEmail.sending') : t('auth:changeEmail.sendLink')}
                </button>
              </div>
            </div>
          )}
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{
            display: 'block',
            marginBottom: '0.5rem',
            fontWeight: '500',
            color: '#333'
          }}>
            {t('labels.language')}
          </label>
          <select
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
            style={{
              width: '100%',
              padding: '0.625rem 0.875rem',
              fontSize: '0.875rem',
              border: '1px solid #ddd',
              borderRadius: '4px',
              boxSizing: 'border-box',
              backgroundColor: 'white'
            }}
          >
            {supportedLanguages.map((lang) => (
              <option key={lang.code} value={lang.code}>
                {lang.nativeName} ({lang.name})
              </option>
            ))}
          </select>
          <p style={{
            marginTop: '0.5rem',
            fontSize: '0.75rem',
            color: 'var(--color-secondary)'
          }}>
            {t('profile.languageHelp')}
          </p>
        </div>

        {attributeDefinitions.length > 0 && (
          <div className="profile-attributes">
            <h4 className="profile-attributes__title">{t('profile.attributesTitle')}</h4>
            <UserAttributeFields
              definitions={attributeDefinitions}
              values={attributeValues}
              onChange={(key, value) => setAttributeValues((prev) => ({ ...prev, [key]: value }))}
              mode="self"
              disabled={loading}
            />
          </div>
        )}

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            style={{
              padding: '0.5rem 1.5rem',
              backgroundColor: 'var(--color-secondary)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {t('buttons.cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '0.5rem 1.5rem',
              backgroundColor: 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {loading ? t('buttons.saving') : t('buttons.save')}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export default ProfileModal
